import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import supabase from '../../../config/supabaseClient';
import { 
  MessageCircle, 
  Search, 
  Filter, 
  Eye, 
  Download,
  Calendar,
  Phone,
  User
} from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

const ListaUsuariosWhatsapp = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    busca: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    carregarUsuarios();
  }, []);

  const carregarUsuarios = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('usuario_whatsapp')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setUsuarios(data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários do WhatsApp:', error);
      toast.error('Erro ao carregar usuários do WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  // Função para obter o nome do usuário (nome_completo ou nome_contato)
  const obterNomeUsuario = (usuario) => {
    return usuario.nome_completo || usuario.nome_contato || 'Nome não informado';
  };

  // Aplicar filtros
  const usuariosFiltrados = usuarios.filter(usuario => {
    // Filtro por busca
    if (filtros.busca) {
      const busca = filtros.busca.toLowerCase();
      const nome = obterNomeUsuario(usuario).toLowerCase();
      return (
        nome.includes(busca) ||
        usuario.telefone?.includes(busca)
      );
    }
    
    return true;
  });

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const limparFiltros = () => {
    setFiltros({
      busca: ''
    });
  };

  const exportarParaExcel = () => {
    const dadosExportacao = usuariosFiltrados.map(usuario => ({
      'Nome': obterNomeUsuario(usuario),
      'Nome Contato': usuario.nome_contato || '',
      'Nome Completo': usuario.nome_completo || '',
      'Telefone': usuario.telefone || '',
      'Data de Cadastro': new Date(usuario.created_at).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      'Última Atualização': usuario.updated_at ? new Date(usuario.updated_at).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }) : ''
    }));

    const ws = XLSX.utils.json_to_sheet(dadosExportacao);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuários WhatsApp');

    const dataAtual = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `usuarios_whatsapp_${dataAtual}.xlsx`);
  };

  if (loading) {
    return (
      <div className="gestao-usuarios lista-full-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando usuários do WhatsApp...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gestao-usuarios lista-full-container">
      <div className="lista-header">
        <div className="header-content">
          <h1>
            <MessageCircle size={32} />
            Usuários do WhatsApp
          </h1>
          <p>Usuários que interagiram via WhatsApp</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={exportarParaExcel}
            className="button secondary"
            disabled={usuariosFiltrados.length === 0}
          >
            <Download size={16} />
            Exportar Excel
          </button>
        </div>
      </div>

      <div className="lista-content-wrapper">
        {/* Barra de busca e filtros */}
        <div className="search-filter-bar">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar por nome ou telefone..."
              value={filtros.busca}
              onChange={(e) => setFiltros(prev => ({ ...prev, busca: e.target.value }))}
            />
          </div>
          <button 
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filtros
          </button>
        </div>

        {/* Painel de filtros */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filter-actions">
              <button className="button secondary" onClick={limparFiltros}>
                Limpar Filtros
              </button>
            </div>
          </div>
        )}

        {/* Estatísticas rápidas */}
        <div className="quick-stats">
          <div className="stat-item">
            <span className="stat-label">Total:</span>
            <span className="stat-value">{usuariosFiltrados.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Com nome completo:</span>
            <span className="stat-value">{usuariosFiltrados.filter(u => u.nome_completo).length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Apenas contato:</span>
            <span className="stat-value">{usuariosFiltrados.filter(u => !u.nome_completo && u.nome_contato).length}</span>
          </div>
        </div>

        {/* Lista de usuários */}
        {usuariosFiltrados.length === 0 ? (
          <div className="empty-state">
            <MessageCircle size={48} />
            <h3>Nenhum usuário encontrado</h3>
            <p>Não há usuários do WhatsApp que correspondam aos filtros aplicados.</p>
          </div>
        ) : (
          <div className="usuarios-list-vertical">
            {usuariosFiltrados.map(usuario => (
              <div key={usuario.id} className="usuario-card-horizontal">
                <div className="card-main-content">
                  <div className="card-header-section">
                    <div className="usuario-info">
                      <h3 className="usuario-title">{obterNomeUsuario(usuario)}</h3>
                      <div className="usuario-meta">
                        {usuario.telefone && (
                          <span className="telefone">
                            <Phone size={14} />
                            {usuario.telefone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="status-badges">
                      {usuario.nome_completo ? (
                        <span className="status-badge status-ativo">
                          <User size={14} />
                          Nome Completo
                        </span>
                      ) : (
                        <span className="status-badge status-warning">
                          <User size={14} />
                          Apenas Contato
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="card-body-section">
                    <div className="info-line">
                      <span className="info-label">Cadastrado:</span>
                      <span className="info-value data-criacao">
                        <Calendar size={14} />
                        {new Date(usuario.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    {usuario.updated_at && (
                      <div className="info-line">
                        <span className="info-label">Última atualização:</span>
                        <span className="info-value">
                          <Calendar size={14} />
                          {new Date(usuario.updated_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                    {usuario.nome_contato && usuario.nome_completo && (
                      <div className="info-line">
                        <span className="info-label">Nome do contato:</span>
                        <span className="info-value">{usuario.nome_contato}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-actions-vertical">
                  <Link 
                    to={`/gestao-usuarios/populacao/whatsapp/detalhes/${usuario.id}`}
                    className="action-button-full view"
                  >
                    <Eye size={16} />
                    Ver Detalhes
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListaUsuariosWhatsapp;